import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Mail, Lock, User, Eye, EyeOff, ArrowLeft, CheckCircle, Clock } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

const Register = () => {
  const { branding } = useBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [oauthStatus, setOauthStatus] = useState({ google: false, microsoft: false });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/oauth/status')
      .then(res => res.json())
      .then(data => setOauthStatus(data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const googleRegisterUrl = '/api/auth/google?action=register';
  const microsoftRegisterUrl = '/api/auth/microsoft?action=register';

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo-wrapper">
              <Clock size={64} style={{ color: '#f59e0b' }} />
            </div>
            <h1>Registration Submitted!</h1>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px', padding: '24px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            <CheckCircle size={32} style={{ color: '#10b981', marginBottom: '16px' }} />
            <p style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px' }}>
              Your registration request has been submitted!
            </p>
            <p style={{ color: '#f59e0b', fontWeight: 500, marginBottom: '16px' }}>{email}</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
              An administrator will review your request and you will be notified via email once your account is approved.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px', padding: '16px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px' }}>
            <p style={{ color: '#93c5fd', fontSize: '14px', margin: 0 }}>
              <strong>What happens next?</strong>
            </p>
            <ul style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>
              <li>An administrator will review your registration</li>
              <li>You'll receive an email when your account is approved</li>
              <li>Once approved, you can log in with your credentials</li>
            </ul>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link to="/login" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <ArrowLeft size={18} />
              Back to Login
            </Link>
          </div>

          <div className="login-footer">
            <p>Questions? Contact support@serverli.com</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="login-page"
      style={branding.brandLoginBg ? { background: branding.brandLoginBg } : undefined}
    >
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo-wrapper">
            <img
              src={branding.brandLogoUrl || '/serverliccm-logo.png'}
              alt={`${branding.brandName} Logo`}
              className="login-logo-img"
            />
          </div>
          <h1>Create Account</h1>
          <p>Register for {branding.brandName} access</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <User size={16} />
              Full Name
            </label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <Mail size={16} />
              Email Address
            </label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <Lock size={16} />
              Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <Lock size={16} />
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading}
          >
            {loading ? 'Submitting...' : <><UserPlus size={18} /> Request Access</>}
          </button>
        </form>

        {(oauthStatus.google || oauthStatus.microsoft) && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '14px', marginBottom: '16px' }}>
              — or register with —
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {oauthStatus.google && (
                <a href={googleRegisterUrl} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}>
                  <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: '18px', height: '18px' }} />
                  Google
                </a>
              )}
              {oauthStatus.microsoft && (
                <a href={microsoftRegisterUrl} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}>
                  <img src="https://www.microsoft.com/favicon.ico" alt="Microsoft" style={{ width: '18px', height: '18px' }} />
                  Microsoft
                </a>
              )}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Link to="/login" style={{ color: '#10b981', textDecoration: 'none', fontSize: '14px' }}>
            Already have an account? Sign in
          </Link>
        </div>

        {(!branding.isWhiteLabeled || branding.poweredByVisible) && (
          <div className={branding.isWhiteLabeled ? 'powered-by-footer' : 'login-footer'}>
            {branding.isWhiteLabeled ? 'Powered by Serverli' : <p>Powered by Serverli</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
